/**
 * Earnings (`/earnings`) — dezelfde acht KPI's, negen tabellen en twee
 * verdelingsblokken als de huidige pagina.
 *
 * Endpoints en parameters exact zoals de bundle ze stuurt (alles volgt het
 * tijdvenster met `days`, default 1 = Today):
 *   dashboard/overview · dashboard/revenue · dashboard/model-performance
 *   dashboard/top-spenders · dashboard/transactions · dashboard/sales-by-chatter
 *   dashboard/payouts · chatter-finance/payouts
 *   chatter-finance/sales-per-chatter-balances
 *   chatter-finance/subscription-pitches?days=&only_pitched=
 *   models/chatters
 */
import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigationType, useSearchParams } from 'react-router-dom'
import { useUiPref } from '../lib/inklappen'
import { bewaarPlek, herstelPlek, plekVoorHier } from '../lib/terugkeer'
import { ErrorNote, Kpi, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { Donut } from '../components/Donut'
import { ModelNaam } from '../components/ModelNaam'
import { ModelFilter, useModelFilter } from '../components/ModelFilter'
import { useApi, usePoll } from '../lib/useApi'
import { isVolledigBeheer, useMij } from '../lib/rechten'
import { ChatterEarnings } from './ChatterEarnings'
import { useActions, mut } from '../lib/actions'
import { PayoutDialog } from '../components/PayoutDialog'
import { KandidaatKnop } from '../components/KandidaatGesprekken'
import { dt, dtShort, eur, num, pct, dtTx, dtDay } from '../lib/format'

interface Overview {
  revenue: { total: number; net_total: number; gross_total: number; events: number
             by_type: Record<string, RevenueSoort> }
}
/** Wat `/api/admin/dashboard/revenue` werkelijk teruggeeft.
 *
 *  `by_type` en `by_handler` bevatten per soort een object {revenue, count} —
 *  geen getal. Dat stond hier verkeerd getypeerd, en omdat TypeScript het dus
 *  goedkeurde toonde "Earnings by Category" overal €0,00: `eur(v)` kreeg een
 *  object in plaats van een bedrag. */
interface RevenueSoort { revenue: number; count: number }
interface Revenue {
  total_revenue: number
  period_method?: string
  daily: { date: string; revenue?: number; events?: number }[]
  by_type: Record<string, RevenueSoort>
  by_handler: Record<string, RevenueSoort>
}
interface ModelPerf {
  id: number; slug: string; avatar_url: string | null
  stage_name: string | null
  fancentro_model_id: string | null; f2f_model_id: string | null
  conversations: number; unique_fans: number
  revenue_gross: number; revenue_net: number
  /** Uit de gedeelde berekening op de server (src/services/modelaandeel.py).
   *  null = geen payout-split ingesteld: onbekend, nooit €0,00. */
  model_share_pct: number | null
  model_fee: number | null
  /** 06-10-2026 (Jordy): platformdeel en agency-deel van de server; rijen van
   *  een platform zonder split voor dit model tellen apart. */
  platform_fee?: number | null
  agency_net?: number | null
  rows_without_split?: number
  /** 07-10-2026 (Jordy): FanCentro en F2F altijd apart. */
  per_platform?: Record<string, PlatformDeel>
}
interface PlatformDeel {
  gross_eur: number; net_eur: number; platform_fee: number
  model_fee: number | null; agency_net: number | null
  rijen_zonder_split: number; model_share_pct: number | null
}
/** Eén regel in Earnings per model: één model op één platform. */
interface ModelRegel {
  m: ModelPerf; plat: string; d: PlatformDeel
}
type Totaal = { gross: number; platform: number; model: number; agency: number; zonder: number }

function isFc(plat: string | null | undefined): boolean {
  const v = String(plat ?? '').toLowerCase()
  return v === 'fancentro' || v.startsWith('fc')
}

/** 07-10-2026 (Jordy): FC "Model share (paid by FanCentro)", F2F "Model share". */
function aandeelLabel(plat: string | null | undefined): string {
  return isFc(plat) ? 'Model share (paid by FanCentro)' : 'Model share'
}

/** Regels per model en platform. Oudere antwoorden zonder `per_platform`:
 *  één regel op het platform van het model, zoals voorheen. */
function regelsVan(m: ModelPerf): ModelRegel[] {
  const pp = m.per_platform
  if (pp && Object.keys(pp).length) {
    return Object.entries(pp)
      .sort(([a], [b]) => (isFc(a) ? 0 : 1) - (isFc(b) ? 0 : 1))
      .map(([plat, d]) => ({ m, plat, d }))
  }
  return [{
    m, plat: m.fancentro_model_id ? 'fancentro' : 'f2f',
    d: {
      gross_eur: m.revenue_gross, net_eur: m.revenue_net, platform_fee: platformVan(m) ?? 0,
      model_fee: m.model_fee, agency_net: m.agency_net ?? null,
      rijen_zonder_split: m.rows_without_split ?? 0, model_share_pct: m.model_share_pct,
    },
  }]
}

function TotaalRij({ label, plat, t, vet }: { label: string; plat?: string; t: Totaal; vet?: boolean }) {
  const stijl = { fontWeight: vet ? 700 : 600, borderTop: '1px solid var(--line, rgba(255,255,255,0.12))' }
  return (
    <tr>
      <td style={stijl}>{label}</td>
      <td style={stijl}>{plat ? <Plat value={plat} /> : null}</td>
      <td className="num mono" style={stijl}>{eur(t.gross)}</td>
      <td className="num mono" style={stijl}>{eur(t.platform)}</td>
      <td className="num mono" style={stijl} title={plat ? aandeelLabel(plat) : 'FanCentro: paid by FanCentro'}>
        {eur(t.model)}
        {t.zonder ? <div className="mut2" style={{ fontSize: 11 }}>excl. {num(t.zonder)} without split</div> : null}
      </td>
      <td className="num mono" style={stijl}>{eur(t.agency)}</td>
      <td style={stijl} /><td style={stijl} />
    </tr>
  )
}

function optellen(regels: ModelRegel[]): Totaal {
  return regels.reduce((a, { d }) => ({
    gross: a.gross + (d.gross_eur || 0),
    platform: a.platform + (d.platform_fee || 0),
    model: a.model + (d.model_fee ?? 0),
    agency: a.agency + (d.agency_net ?? 0),
    zonder: a.zonder + (d.rijen_zonder_split || 0),
  }), { gross: 0, platform: 0, model: 0, agency: 0, zonder: 0 })
}

/** 30-09-2026 (Jordy): een model zonder payout-split heeft een onbekend
 *  aandeel. Dat staat er dan ook, in plaats van €0,00 of een streepje. */
function NogNietBekend() {
  return <span className="mut" title="No payout split set for this model and platform">Split not set</span>
}

/** 11-10-2026 (Jordy): zonder omzet in het venster is er niets te verdelen:
 *  dan €0,00. "Split not set" alleen als er wél omzet is zonder split. */
function aandeelGeld(v: number | null | undefined, bruto: number | null | undefined): string | JSX.Element {
  if (v == null && !(Number(bruto) > 0)) return eur(0)
  return modelGeld(v)
}

/** De waarde van een tegel "Model share …" voor één platform. */
function tegelAandeel(regels: ModelRegel[], totaal: number): string {
  const metOmzet = regels.filter(({ d }) => Number(d.gross_eur) > 0)
  if (!metOmzet.length) return eur(0)
  if (metOmzet.every(({ d }) => d.model_fee == null)) return 'Split not set'
  return eur(totaal)
}

/** Modelaandeel zoals de server het gaf, of "Split not set". */
function modelGeld(v: number | null | undefined): string | JSX.Element {
  return v == null ? <NogNietBekend /> : eur(v)
}

/** Het platformdeel van de server (06-10-2026: 20%, bij FanCentro het echte
 *  deel uit het netto). Oudere antwoorden: bruto − netto − modelaandeel. */
function platformVan(m: ModelPerf): number | null {
  if (m.platform_fee != null) return m.platform_fee
  return m.model_fee == null ? null : Math.max(0, m.revenue_gross - m.revenue_net - m.model_fee)
}

interface Spender {
  fan_id: string; username: string; model: string
  gross_spend: number; net_spend: number
}
/** Zoals `dashboard/transactions` het werkelijk teruggeeft.
 *
 *  De eerdere namen (`fan`, `model`, `amount`, `net_amount`) bestaan in dat
 *  antwoord niet; daardoor stonden Fan, Model, Chatter op "—" en alle
 *  bedragen op €0,00 terwijl de API ze wél leverde. */
interface Txn {
  id: number
  created_at: string
  sale_at_utc: string | null
  platform: string
  event_type: string
  fan_username: string | null
  fan_platform_id: string | null
  model_name: string | null
  model_slug: string | null
  assigned_chatter_name: string | null
  suggested_chatter_name: string | null
  gross: number | null
  model_fee: number | null
  platform_fee: number | null
  net: number | null
  /** EARN-006: false = de bron geeft geen splitsing, netto is onbekend. */
  net_bekend?: boolean | null
  model_share_pct: number | null
  leftover: number | null
  conversation_id: number | null
  /** 20-10-2026: "gekoppeld", "ontgrendeling" (F2F-koper via de ontgrendeling, alleen voor de link) of null. */
  conversation_via?: string | null
  /** 23-10-2026 (Jordy): het gekochte bericht (betaalde content), voor de directe sprong. */
  paid_message_id?: number | null
  reviewed: boolean | null
  assigned_chatter_id: number | null
  assigned_chatter_source: string | null
  /** EARN-004: een mens koos dit (chatter, organic of nog niet toegewezen). */
  assigned_manual?: boolean | null
  /** EARN-004B: telt deze verkoop mee voor commissie en referral? */
  counts_for_commission?: boolean | null
  assigned_by_name?: string | null
  assigned_at?: string | null
  unassigned_reason?: string | null
}
/** Een bedrag dat de bron niet geeft, tonen we als streepje — niet als 0,00.
 *  16-09-2026 (EARN-006): zonder gescrapete splitsing zijn netto, modelfee en
 *  platformfee onbekend; commissie valt dan ook niet te berekenen. */
/** ISO-tijd → ms, zonder tijdzone gelezen als UTC (de server geeft naive UTC). */
function utcMs(iso: string): number {
  return Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`)
}

function geld(waarde: number | null | undefined): string | JSX.Element {
  return waarde == null ? <span className="mut">—</span> : eur(waarde)
}

/** Welke optie hoort bij de toewijzing zoals de server hem teruggeeft. */
function keuzeVanServer(t: Txn): string {
  if (t.assigned_chatter_source === 'organic') return 'organic'
  if (t.assigned_chatter_source === 'unassigned') return 'unassigned'
  return t.assigned_chatter_id != null ? String(t.assigned_chatter_id) : 'auto'
}

/** De fannaam als link naar het gesprek, op het moment van de verkoop.
 *  29-09-2026 (Jordy): zoals op oud, vanuit Earnings door naar het exacte
 *  gesprek. Het chatvenster springt met `?op=` naar het bericht rond dat
 *  tijdstip. Zonder gesprek opent de naam de kandidaat-gesprekken. */
function NaarGesprek({ t, children }: { t: Txn; children: ReactNode }) {
  const op = t.sale_at_utc ?? t.created_at
  // 20-10-2026 (Jordy): geen gesprek = geen dode klik: de naam opent de
  // kandidaat-gesprekken van dit model (of de reden dat er geen zijn).
  if (!t.conversation_id) {
    return <KandidaatKnop eventId={Number(t.id)} op={op} onOpen={() => bewaarPlek(t.id)}>{children}</KandidaatKnop>
  }
  // 23-10-2026 (Jordy): betaalde content springt direct naar het gekochte bericht.
  const doel = t.paid_message_id
    ? `?msg=${t.paid_message_id}`
    : (op ? `?op=${encodeURIComponent(op)}` : '')
  return (
    <Link to={`/conversations/show/${t.conversation_id}${doel}`}
          // 11-10-2026: plek en rij onthouden, zodat "terug" hier weer uitkomt.
          onClick={(e) => bewaarPlek(t.id, e.currentTarget)}
          title={t.paid_message_id
            ? 'Open the chat at the paid message that was bought'
            : t.conversation_via === 'ontgrendeling'
              ? 'Open the chat of the message that was unlocked at the time of this sale (F2F buyer known by display name only)'
              : 'Open this chat at the time of the sale'}>
      {children}
    </Link>
  )
}

interface Balance {
  chatter_id: number; commission_pct: number | null; own_commission: number
  /** 30-09-2026: naam en of de chatter verwijderd is (de roster kent hem dan niet meer). */
  name?: string | null; deleted?: boolean
  referral_earned: number; open_commission: number; paid_commission: number
  earned_commission_alltime: number
  /** Het openstaande bedrag gesplitst in eigen commissie en referral —
   *  precies de twee getallen die oud onder Open toont. */
  advance_remaining?: number | null
  open_own_commission: number; open_referral_commission: number
}
interface ChatterPayout {
  id: number; chatter_id: number; period_start: string; period_end: string
  gross_amount: number; commission_amount: number; referral_amount: number
  amount_paid: number; extra_is_bonus: boolean; paid_at: string | null
  note: string | null; status: string
}
interface PlatformPayout {
  id: number; platform: string; period_start: string; period_end: string
  paid_at: string | null; amount_gross: number; amount_net: number
  status: string; has_document: boolean
}
/** Eén chatter binnen het gekozen venster. */
interface SalesChatter {
  chatter_id: number; name: string; sales: number
  deleted?: boolean
  gross: number; net: number
  own_commission: number | null; commission_amount?: number | null
  referral_earned: number
  commission_pct: number | null; is_lead: boolean
  lead_chatter_id: number | null
  lead_override_pct: number | null
}
interface SalesByChatter {
  total_sales: number; attributed_sales: number
  /** verkoop-id -> chatter-id, volgens dezelfde regel als de KPI-kaarten. */
  per_event?: Record<string, number>
  /** EARN-004B: de overgangsdag, of null zolang het oude dashboard uitbetaalt. */
  commission_since?: string | null
  commission_notice?: string | null
  /** De verkopen die aan niemand toegewezen zijn — eigen regel in de tabel. */
  unattributed: { sales: number; gross: number; net: number } | null
  /** 11-10-2026 (Jordy/Zilvester): het derde bakje — verkopen zonder chatter die
   *  ook niet als chatverkoop toe te rekenen zijn (abonnementen zonder
   *  toewijzing). Zo sluit total_sales altijd. */
  not_attributable?: { sales: number; gross: number; net: number; per_type?: Record<string, number> } | null
  per_chatter: SalesChatter[]
}
/** Wat /chatter-finance/subscription-pitches werkelijk teruggeeft.
 *
 *  Stond hier met verzonnen veldnamen (subscription_id, fan, model, gross, …).
 *  De API heet ze anders, dus las de tabel overal `undefined` en toonde €0,00
 *  met lege kolommen. */
interface Pitches {
  auto_assign_enabled: boolean
  summary: { total: number; pitched: number; ambiguous: number; assigned: number }
  rows: {
    revenue_event_id: number
    fan_handle: string | null
    fan_display_name: string | null
    model_stage_name: string | null
    amount: number
    pitch_chatter_id: number | null
    pitch_chatter_name: string | null
    pitch_text: string | null
    pitch_at: string | null
    pitch_gap_label?: string | null
    conversation_id?: number | null
    is_ambiguous: boolean
    pitching_chatter_ids?: number[]
    pitching_chatter_names?: string[]
    assigned_chatter_id?: number | null
    assigned_chatter_name: string | null
    auto_assigned?: boolean
    /** Alle pitch-berichten in het venster, oudste eerst (de "i/N"-keuze). */
    pitches?: {
      message_id: number
      conversation_id: number | null
      chatter_id: number
      chatter_name: string
      text: string | null
      created_at: string | null
      gap_label: string | null
    }[]
  }[]
}

/** Engelse labels per omzetsoort, zoals oud ze toont. Dezelfde lijst als
 *  `CATEGORIE_LABELS` in src/services/earnings_math.py — één plek waar de naam
 *  van een soort wordt bepaald. */
const CATEGORIE_LABELS: Record<string, string> = {
  chat_message: 'Paid Message',
  subscription: 'Subscription',
  tip: 'Tip',
  chat_attachment: 'Chat Attachment',
  dm_unlock: 'DM Unlock',
  video_unlock: 'Video Unlock',
  post_unlock: 'Post Unlock',
  post: 'Post',
  custom: 'Custom Content',
}

/** De kleuren van oud: oranje voor bruto, groen voor netto, roze voor de
 *  chatter/referral-kant, blauw voor het modeldeel. Jordy wil die houden. */
const DONUT_KLEUREN = [
  'var(--acc, #f0883e)', 'var(--ok, #3fb950)', 'var(--pink, #db61a2)',
  'var(--blue, #58a6ff)', 'var(--warn, #d29922)', 'var(--mut, #8b949e)',
]

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]
const RANGES = [
  { value: '1', label: 'Today' }, { value: '7', label: 'Week' },
  { value: '30', label: 'Month' }, { value: '90', label: 'Quarter' },
  { value: '365', label: 'Year' },
]
/** 11-10-2026 (Jordy): de periode waarmee Earnings opent, per gebruiker
 *  (admin_users.ui_prefs). Zonder keuze: Today, zoals altijd. */
const PREF_STANDAARD = 'earnings.standaard_dagen'
const GELDIGE_DAGEN = new Set(RANGES.map((x) => x.value))
const DOCS = [
  { value: 'payouts', label: 'Payouts' },
  { value: 'invoices', label: 'Invoices / VAT' },
]

/** "chat_attachment" → "Chat Attachment", zoals oud het toont. */
function typeLabel(v: unknown): string {
  return String(v ?? '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}


/**
 * Bedrag met een PUNT als decimaalteken: "€397.49".
 *
 * Oud gebruikt overal zijn centrale helper met nl-NL ("€254,90"), behalve in
 * de twee tabellen hieronder — Sales per chatter en de betaalgeschiedenis —
 * die zelf "€" + toFixed(2) samenstellen en daardoor Engels uitkomen, zonder
 * duizendtalscheiding. Die eigenaardigheid is hier bewust nagebouwd: de
 * opmaak volgt oud per blok, niet per site.
 */
function eurPunt(value: unknown): string {
  const n = Number(value ?? 0)
  return '€' + (Number.isFinite(n) ? n : 0).toFixed(2)
}

/**
 * Wie welk scherm krijgt.
 *
 * Dit MOET de splitsing zijn en niet een `return` halverwege `EarningsBeheer`.
 * 10-09-2026: daar stond hij eerst, en React telt haken per render. Zolang
 * `/auth/me` nog liep was `mij.data` leeg en liepen alle haken; zodra het
 * antwoord binnen was sloeg de uitstap toe en liepen er minder. Dat is React
 * error #300 — "rendered fewer hooks than expected" — en die neemt de HELE
 * boom mee. Jordy zag een zwart scherm.
 *
 * Twee losse componenten hebben elk hun eigen, altijd gelijke lijst haken.
 */
export default function Earnings() {
  const mij = useMij()
  // 11-10-2026: de eigen standaardperiode moet binnen zijn voordat het
  // beheerscherm begint te laden, anders laadt het eerst Today en springt dan.
  const [, , prefKlaar] = useUiPref<string>(PREF_STANDAARD, '1')
  // Zolang we niet weten wie er kijkt tonen we een leeg kader — geen half
  // beheerscherm dat meteen daarna van vorm verandert.
  if (!mij.data || !prefKlaar) return <div />
  // 05-10-2026 (Jordy): een manager krijgt My Earnings, niet het beheerscherm.
  if (!isVolledigBeheer(mij.data)) {
    return <ChatterEarnings chatterId={mij.data.linked_chatter_id ?? null} />
  }
  return <EarningsBeheer />
}


function EarningsBeheer() {
  // 11-10-2026 (Jordy): periode en platform staan in het adres (net als het
  // modelfilter), zodat "terug" vanuit een gesprek dezelfde weergave geeft.
  // Zonder periode in het adres: de eigen standaardperiode.
  const [zoek, setZoek] = useSearchParams()
  const [standaardDagen, zetStandaardDagen] = useUiPref<string>(PREF_STANDAARD, '1')
  const uitAdres = zoek.get('days')
  const days = uitAdres && GELDIGE_DAGEN.has(uitAdres)
    ? uitAdres : (GELDIGE_DAGEN.has(standaardDagen) ? standaardDagen : '1')
  const platform = ['fancentro', 'f2f'].includes(zoek.get('platform') ?? '') ? zoek.get('platform')! : 'all'
  const zetAdres = (sleutel: string, waarde: string, weg: string) => {
    const nieuw = new URLSearchParams(zoek)
    if (waarde === weg) nieuw.delete(sleutel)
    else nieuw.set(sleutel, waarde)
    setZoek(nieuw, { replace: true })
  }
  const setDays = (v: string) => zetAdres('days', v, '')
  const setPlatform = (v: string) => zetAdres('platform', v, 'all')
  /** De rij die je het laatst opende; gemarkeerd als je terugkomt. */
  const [laatsteRij, setLaatsteRij] = useState<string | null>(null)
  const navigatie = useNavigationType()
  const [docTab, setDocTab] = useState('payouts')
  const [onlyPitched, setOnlyPitched] = useState(false)
  // 13-10-2026 (Jordy): zoals v1 — per abonnement welk pitch-bericht je bekijkt,
  // en de auto-assign-knop alleen voor de eigenaar.
  const [pitchKeuze, setPitchKeuze] = useState<Record<number, number>>({})
  const ik = useMij()
  const isOwner = String(ik.data?.role ?? '').toLowerCase() === 'owner'
  const [payFor, setPayFor] = useState<number | null>(null)
  const { run, busy } = useActions()

  /** Documentknop: het PDF-antwoord in een nieuw tabblad tonen, zoals de bundle. */
  async function downloadDoc(id: number) {
    const r = await fetch(`/api/admin/dashboard/payouts/${id}/download`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_access_token') || ''}` },
    })
    if (!r.ok) { alert(`Download failed (HTTP ${r.status})`); return }
    if ((r.headers.get('content-type') || '').includes('pdf')) {
      window.open(URL.createObjectURL(await r.blob()), '_blank')
    } else {
      const j = await r.json().catch(() => null) as { url?: string } | null
      if (j?.url) window.open(j.url, '_blank')
      else alert('No document available.')
    }
  }

  // 04-10-2026 (Jordy): filter op één model geldt voor de hele pagina, ook de
  // totalen; per pagina gekozen, in het adres. Saldi en uitbetalingen van
  // chatters zijn geen modelcijfers en blijven ongefilterd.
  const filter = useModelFilter()
  const p = (platform === 'all' ? '' : `&platform=${platform}`) + filter.param
  // `p` moet hier ook mee: zonder platformfilter toonde Total Revenue het totaal
  // van beide platforms terwijl de categorielijst eronder wél gefilterd was.
  const ov = useApi<Overview>(`/api/admin/dashboard/overview?days=${days}${p}`,
    [days, platform, filter.param])
  const rev = useApi<Revenue>(`/api/admin/dashboard/revenue?days=${days}${p}`, [days, platform, filter.param])
  const perf = useApi<ModelPerf[]>(`/api/admin/dashboard/model-performance?days=${days}${filter.param}`,
    [days, filter.param])
  const spenders = useApi<{ spenders: Spender[]; total: number }>(
    `/api/admin/dashboard/top-spenders?days=${days}&limit=150${filter.param}`, [days, filter.param])
  const txns = useApi<{ items: Txn[]; total: number }>(
    `/api/admin/dashboard/transactions?days=${days}&limit=1000${p}`, [days, platform, filter.param])

  // 11-10-2026 (Jordy): terug vanuit een gesprek = dezelfde plek en rij.
  // Alleen bij "terug" (POP) en alleen voor precies dit adres.
  useEffect(() => {
    if (navigatie !== 'POP') return
    const plek = plekVoorHier()
    if (!plek) return
    setLaatsteRij(plek.rij)
    if (txns.loading && !txns.data) return
    return herstelPlek(plek)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigatie, txns.loading])
  const sales = useApi<SalesByChatter>(
    `/api/admin/dashboard/sales-by-chatter?days=${days}${p}`, [days, platform, filter.param])
  const comp = useApi<{ chatter_id: number; name: string; is_lead: boolean;
    lead_chatter_id: number | null; lead_override_pct: number | null;
    // 24-10-2026 (Jordy): de koppelperiodes; een verkoop telt voor de head
    // chatter van het verkoopmoment, en alleen na referral_since.
    referral_since?: string | null
    referral_periods?: { lead_chatter_id: number; since: string; until: string | null }[]
    head_pct_periods?: { pct: number; since: string; until: string | null }[]
    // Het betaalvenster toont hiermee waar naartoe betaald wordt, of dat er
    // nog geen wallet staat. Owner-only endpoint: bij 403 blijft dit leeg.
    payout_wallet_address: string | null; payout_wallet_network: string | null }[]>(
    '/api/admin/chatter-finance/compensation-roster')
  const balances = useApi<{ per_chatter: Balance[]; commission_since?: string | null }>(
    '/api/admin/chatter-finance/sales-per-chatter-balances')
  const cpay = useApi<{ items: ChatterPayout[] }>('/api/admin/chatter-finance/payouts')
  const ppay = useApi<{ items: PlatformPayout[] }>('/api/admin/dashboard/payouts')
  const pitches = useApi<Pitches>(
    `/api/admin/chatter-finance/subscription-pitches?days=${days}&only_pitched=${onlyPitched}`,
    [days, onlyPitched])
  const chatters = useApi<{ id: number; name: string }[]>('/api/admin/models/chatters')



  const nameOf = useMemo(() => {
    const m = new Map<number, string>()
    for (const c of chatters.data ?? []) m.set(c.id, c.name)
    // 30-09-2026 (Jordy): een verwijderde chatter staat niet meer in de
    // roster, maar kan nog geld open hebben staan. Zijn naam komt dan uit de
    // saldi, met "(deleted)", zodat Record payment hem nog bij naam kent.
    for (const b of balances.data?.per_chatter ?? []) {
      if (!m.has(b.chatter_id) && b.name) m.set(b.chatter_id, b.deleted ? `${b.name} (deleted)` : b.name)
    }
    return m
  }, [chatters.data, balances.data])
  /** Verwijderde chatters zonder open bedrag standaard verbergen (30-09-2026). */
  const [toonVerwijderd, setToonVerwijderd] = useState(false)

  /** Optellen over de chatters van het venster, resp. over de saldi. Twee
   *  bronnen, dus twee hulpjes — oud telt de totaalregel net zo op. */
  const som = (f: (x: SalesChatter) => number | undefined | null): number =>
    (sales.data?.per_chatter ?? []).reduce((a, x) => a + Number(f(x) || 0), 0)
  const somSaldo = (f: (b: Balance) => number | undefined | null): number =>
    (balances.data?.per_chatter ?? []).reduce((a, b) => a + Number(f(b) || 0), 0)

  /** Commissiepercentage per chatternaam; de transactietabel heeft het per
   *  rij nodig en de balansen leveren het al. */
  /** Lead-percentage per chatternaam. Oud toont in de kolom Referral het
   *  deel dat naar de lead van die chatter gaat: 5% van het netto bedrag.
   *  Heeft een chatter geen lead, dan blijft de kolom leeg. */
  const leadPct = useMemo(() => {
    const rows = comp.data ?? []
    // De referral is het percentage van de LEAD, niet van de verkoper zelf.
    // Baki en Banki hebben lead_chatter_id 25 (Max) en zelf 0%; Max is de
    // lead met 5%. Het percentage van de verkoper aflezen gaf daarom overal
    // nul, terwijl oud €0,19 en €0,03 toont.
    // 24-10-2026 (Jordy): het % per periode — de verkoop krijgt het % van de
    // head chatter dat op het verkoopmoment gold (vanaf inclusief).
    const pctPeriodes = new Map<number, { pct: number; since: number; until: number | null }[]>()
    for (const c of rows) {
      pctPeriodes.set(c.chatter_id, (c.head_pct_periods ?? []).map((p) => ({
        pct: Number(p.pct), since: utcMs(p.since), until: p.until ? utcMs(p.until) : null,
      })))
    }
    const pctVanLead = (lead: number, t: number): number =>
      (pctPeriodes.get(lead) ?? []).find((p) => t >= p.since && (p.until == null || t < p.until))?.pct ?? 0
    // 24-10-2026 (Jordy): per verkoopmoment — de head chatter van dat moment,
    // en niets vóór referral_since (strikt erna) of na referral_until.
    const periodes = new Map<string, { lead: number; since: number; until: number | null }[]>()
    for (const c of rows) {
      periodes.set(c.name, (c.referral_periods ?? []).map((v) => ({
        lead: v.lead_chatter_id,
        since: utcMs(v.since),
        until: v.until ? utcMs(v.until) : null,
      })))
    }
    return {
      get(naam: string, moment?: string | null): number {
        if (!moment) return 0
        const t = utcMs(moment)
        const v = (periodes.get(naam) ?? []).find((p) => t > p.since && (p.until == null || t < p.until))
        return v ? pctVanLead(v.lead, t) : 0
      },
    }
  }, [comp.data])

  /**
   * Een verkoop handmatig toewijzen, of terugzetten naar de automatische regel.
   *
   * 14-09-2026 (EARN-004). Voorheen stuurden "Not assigned yet" en "Organic"
   * allebei een DELETE; daarna zette de automatische regel dezelfde chatter
   * terug en leek opslaan niet te werken. Nu:
   *
   *   "unassigned" / "organic" -> POST sale-override {chatter_id: null, kind}
   *                               — overstemt de automatische regel
   *   chatter                  -> POST sale-override {chatter_id, reviewed}
   *                               — dezelfde chatter als de regel = bevestiging
   *   "auto"                   -> DELETE sale-override/{id}
   *                               — terug naar de regel, vastgelegd in de audit
   *
   * Na afloop worden de transacties én de balansen opnieuw opgehaald, want
   * Open/Paid en "Sales per chatter" rekenen mee.
   */
  /** De keuze die net is gemaakt, per verkoop, tot de server hem bevestigt.
   *
   *  16-09-2026 (Jordy, TAAK 1): een verkoop van "Max" op "Not assigned"
   *  zetten gaf rechtsonder "opgeslagen", maar de keuzelijst bleef "Max"
   *  tonen. Twee oorzaken: de melding komt vóór het opnieuw ophalen, en tot
   *  die gegevens binnen zijn stond de oude waarde nog op het scherm. De
   *  keuzelijst toont daarom nu meteen de gemaakte keuze, en laat die pas los
   *  zodra de server hetzelfde antwoordt (of na 15 seconden, zodat een
   *  mislukte opslag nooit blijft plakken).
   */
  const [lokaleKeuze, setLokaleKeuze] = useState<Record<number, { keuze: string; sinds: number }>>({})

  useEffect(() => {
    if (!Object.keys(lokaleKeuze).length) return
    const nu = Date.now()
    const serverKeuze = new Map(
      (txns.data?.items ?? []).map((t) => [t.id, keuzeVanServer(t)] as const),
    )
    const over = Object.fromEntries(
      Object.entries(lokaleKeuze).filter(([id, v]) => {
        if (nu - v.sinds > 15000) return false
        const server = serverKeuze.get(Number(id))
        return server === undefined || server !== v.keuze
      }),
    )
    if (Object.keys(over).length !== Object.keys(lokaleKeuze).length) setLokaleKeuze(over)
  }, [txns.data, lokaleKeuze])

  const wijsToe = (t: Txn, keuze: string) => {
    setLokaleKeuze((vorige) => ({ ...vorige, [t.id]: { keuze, sinds: Date.now() } }))
    return run({
      label: `assign-${t.id}`,
      request: keuze === 'auto'
        ? mut.del(`/api/admin/chatter-finance/sale-override/${t.id}`)
        : keuze === 'unassigned' || keuze === 'organic'
          ? mut.post('/api/admin/chatter-finance/sale-override', {
              revenue_event_id: t.id,
              chatter_id: null,
              kind: keuze,
              reason: keuze === 'organic'
                ? 'Reviewed from Earnings: no chatter / organic'
                : 'Reviewed from Earnings: not assigned yet',
            })
          : mut.post('/api/admin/chatter-finance/sale-override', {
              revenue_event_id: t.id,
              chatter_id: Number(keuze),
              reason: 'Reviewed from Earnings: manual assignment',
              reviewed: true,
            }),
      onDone: () => { txns.refetch(); balances.refetch(); sales.refetch() },
      successText: keuze === 'auto' ? 'Back to the automatic rule.' : 'Sale assignment saved.',
    }).then((gelukt) => {
      // Mislukt? Dan de keuzelijst meteen terug op wat de server zegt.
      if (!gelukt) {
        setLokaleKeuze(({ [t.id]: _weg, ...rest }) => rest)
      }
      return gelukt
    })
  }

  /** Geen overgangsdatum = commissie telt nog nergens mee (EARN-004B). */
  const commissieUit = sales.data ? !sales.data.commission_since : false

  const commissiePct = useMemo(() => {
    const m = new Map<string, number>()
    for (const b of balances.data?.per_chatter ?? []) {
      const naam = nameOf.get(b.chatter_id)
      if (naam) m.set(naam, Number(b.commission_pct ?? 0))
    }
    return m
  }, [balances.data, nameOf])


  const models = useMemo(() => {
    return (perf.data ?? []).filter((m) =>
      platform === 'all' ? true
      : platform === 'fancentro' ? !!m.fancentro_model_id
      : !!m.f2f_model_id)
  }, [perf.data, platform])

  const r = ov.data?.revenue
  // 07-10-2026 (Jordy): elk model een aparte regel per platform, ook bij "All".
  const regels = useMemo(() => models.flatMap(regelsVan)
    .filter(({ plat }) => platform === 'all' ? true
      : platform === 'fancentro' ? isFc(plat) : !isFc(plat)), [models, platform])
  const regelsFc = regels.filter(({ plat }) => isFc(plat))
  const regelsF2f = regels.filter(({ plat }) => !isFc(plat))
  const totFc = optellen(regelsFc)
  const totF2f = optellen(regelsF2f)
  const totAlles = optellen(regels)
  /** De verkoper-van-record volgens dezelfde regel als de kaarten.
   *
   *  Sep 8 (beslissing): één toewijzingsregel voor de hele pagina. De tabel
   *  loste de chatter voorheen zelf op met voorkeur voor het laatste betaalde
   *  bericht, en kwam daardoor op een andere chatter uit dan de kaart erboven —
   *  8 van de 10 verkopen toegerekend tegen 6, en dezelfde verkoop bij een
   *  andere persoon. `/sales-by-chatter` geeft nu `per_event` mee: de uitkomst
   *  van de kaartregel per verkoop. De tabel volgt die.
   *
   *  Gevolg, bewust: de totaalrij van de tabel toont niet langer 5,01 en 1,32
   *  zoals oud, maar 3,91 en 0,57 — gelijk aan de kaarten. Zie
   *  /root/earnings-spec.md voor de afweging. */
  const verkoperVan = useMemo(() => {
    const uit = new Map<number, string>()
    const pe = sales.data?.per_event ?? {}
    const naamVan = new Map((sales.data?.per_chatter ?? [])
      .map((c) => [Number(c.chatter_id), c.name] as const))
    for (const [eventId, chatterId] of Object.entries(pe)) {
      const naam = naamVan.get(Number(chatterId))
      if (naam) uit.set(Number(eventId), naam)
    }
    return uit
  }, [sales.data])

  /** Chatter earnings en Referral over HET GEKOZEN VENSTER.
   *
   *  Stond hier op `balances` — `/chatter-finance/sales-per-chatter-balances`,
   *  dat zonder enige datumparameter wordt aangeroepen en dus de saldi over alle
   *  tijd teruggeeft. Op Week gaf dat €93,81 en €8,79 naast een Net van €46,95
   *  uit dezelfde rij, en dus een Net-net van €-55,65: een negatieve winst die
   *  alleen bestond omdat twee kaarten naast elkaar een ander tijdvak toonden.
   *
   *  `sales` is dezelfde berekening over hetzelfde venster als de rest van de
   *  pagina (`/dashboard/sales-by-chatter?days=…`), en is de bron die het
   *  parity-harnas controleert. De saldi blijven staan voor Payment history —
   *  daar hóórt een stand over alle tijd. */
  const chatterEarned = (sales.data?.per_chatter ?? [])
    .reduce((a, b) => a + (b.own_commission || 0), 0)
  const referral = (sales.data?.per_chatter ?? [])
    .reduce((a, b) => a + (b.referral_earned || 0), 0)

  return (
    <>
      {/* EARN-004B, besluit Jordy 14-09-2026: tot de overgangsdag loopt de
          uitbetaling via het oude dashboard. */}
      {sales.data?.commission_notice ? (
        <div className="mut2" role="note"
             style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8,
                      border: '1px solid rgba(245,158,11,0.4)',
                      background: 'rgba(245,158,11,0.10)', fontSize: 12.5 }}>
          {sales.data.commission_notice}
        </div>
      ) : null}
      <OnbeheerdBalk />
      <div className="filters">
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <Seg options={RANGES} value={days} onChange={setDays} />
        <label className="mut2" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}
               title="The period Earnings opens with, for your account">
          Default
          <select className="select" value={standaardDagen} aria-label="Default period"
                  onChange={(e) => zetStandaardDagen(e.target.value)}>
            {RANGES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </select>
        </label>
        <ModelFilter filter={filter} />
        <div className="spacer" style={{ flex: 1 }} />
        <button
          className="btn ghost sm"
          type="button"
          disabled={busy === 'Import FC'}
          onClick={() => run({
            label: 'Import FC',
            confirm: 'FanCentro-omzet importeren?',
            request: mut.post('/api/admin/dashboard/earnings/import'),
            onDone: () => { ov.refetch(); rev.refetch(); txns.refetch() },
            successText: 'FC import started.',
          })}
        >
          Import FC
        </button>
        <button
          className="btn ghost sm"
          type="button"
          disabled={busy === 'Import F2F'}
          onClick={() => run({
            label: 'Import F2F',
            confirm: 'F2F-omzet importeren?',
            request: mut.post('/api/admin/dashboard/earnings/import-f2f'),
            onDone: () => { ov.refetch(); rev.refetch(); txns.refetch() },
            successText: 'F2F import started.',
          })}
        >
          Import F2F
        </button>
      </div>

      <div className="grid kpis">
        <Kpi icon="€" label="Total Revenue" value={eur(r?.gross_total)} meta="gross in this window" />
        {/* 07-10-2026 (Jordy): FC en F2F apart in de tegels. */}
        {platform !== 'f2f' ? (
          <Kpi icon="◍" label="Model share FC"
               value={tegelAandeel(regelsFc, totFc.model)}
               meta={totFc.zonder ? `paid by FanCentro · excl. ${num(totFc.zonder)} sales without split` : 'paid by FanCentro'} />
        ) : null}
        {platform !== 'fancentro' ? (
          <Kpi icon="◍" label="Model share F2F"
               value={tegelAandeel(regelsF2f, totF2f.model)}
               meta={totF2f.zonder ? `excl. ${num(totF2f.zonder)} sales without split` : 'Model share'} />
        ) : null}
        <Kpi icon="%" label="Platform fees" value={eur(totAlles.platform)}
             meta={platform === 'all' ? `FC ${eur(totFc.platform)} · F2F ${eur(totF2f.platform)}` : 'Platform’s cut on sales'} />
        <Kpi icon="∑" label="Net (before chatter)" value={eur(r?.net_total)} meta="After platform + model" />
        <Kpi icon="☺" label="Chatter earnings" value={eur(chatterEarned)} meta="Commission on assigned chats" />
        <Kpi icon="↳" label="Referral commission" value={eur(referral)} meta="Paid to head chatters" />
        <Kpi icon="✓" label="Net-net" value={eur((r?.net_total ?? 0) - chatterEarned - referral)}
             meta="After chatter + referral" />
        <Kpi icon="⇄" label="Transactions" value={num(r?.events)} meta="in this window" />
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <Panel inklap="earnings.categorie" title="Earnings by Category">
          {Object.keys(rev.data?.by_type ?? {}).length === 0 ? (
            <div className="empty">
              No earnings data yet. Revenue events appear as transactions occur.
            </div>
          ) : (
            <div className="bars">
              {/* `by_type` geeft per soort een object {revenue, count}, geen
                  getal. Dit stond op `eur(v)` en toonde daardoor overal €0,00.
                  Label, bedrag, aantal en aandeel staan er nu bij, zoals oud. */}
              {Object.entries(rev.data!.by_type)
                .map(([k, v]) => ({
                  key: k,
                  label: CATEGORIE_LABELS[k] ?? k.replace(/_/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase()),
                  bedrag: Number(v?.revenue ?? 0),
                  aantal: Number(v?.count ?? 0),
                }))
                .sort((a, b) => b.bedrag - a.bedrag)
                .map((rij) => {
                  const max = Math.max(
                    ...Object.values(rev.data!.by_type).map((x) => Number(x?.revenue ?? 0)), 1)
                  const totaal = Number(r?.gross_total ?? 0)
                  const aandeel = totaal ? (rij.bedrag / totaal) * 100 : 0
                  return (
                    <div className="bar" key={rij.key}>
                      <span className="mut">{rij.label}</span>
                      <div className="track">
                        <div className="fill"
                             style={{ width: `${(rij.bedrag / max) * 100}%` }} />
                      </div>
                      <span className="num mono">{eur(rij.bedrag)}</span>
                      <span className="mut2" style={{ fontSize: 12, minWidth: 92,
                                                      textAlign: 'right' }}>
                        {num(rij.aantal)}× · {aandeel.toFixed(1)}%
                      </span>
                    </div>
                  )
                })}
            </div>
          )}
        </Panel>
        <Panel inklap="earnings.verdeling" title="Revenue Distribution">
          {/* Stond op een lijngrafiek over `daily` met yKey="net" — dat veld
              bestaat daar niet, dus het paneel bleef leeg. Oud toont hier een
              donut per categorie met de omzet in het midden. */}
          {Object.keys(rev.data?.by_type ?? {}).length === 0 ? (
            <div className="empty">No data yet</div>
          ) : (
            <Donut
              title={eur(r?.gross_total)}
              segments={Object.entries(rev.data!.by_type)
                .map(([k, v], i) => ({
                  label: CATEGORIE_LABELS[k] ?? k.replace(/_/g, ' '),
                  value: Number(v?.revenue ?? 0),
                  color: DONUT_KLEUREN[i % DONUT_KLEUREN.length],
                }))
                .sort((a, b) => b.value - a.value)}
            />
          )}
        </Panel>
      </div>

      <Panel inklap="earnings.per-model" title="Earnings per Model"
             desc="One row per model per platform. FanCentro pays its models directly." flush>
        {perf.loading ? <Loading /> : perf.error ? <ErrorNote error={perf.error} /> : (
          <Table cols={[
            { key: 'm', label: 'Model' }, { key: 'p', label: 'Platform' },
            { key: 'g', label: 'Gross', num: true },
            { key: 'pf', label: 'Platform share', num: true },
            { key: 'mf', label: 'Model share', num: true },
            { key: 'a', label: 'Agency', num: true },
            { key: 'c', label: 'Conversations', num: true },
            { key: 'f', label: 'Fans', num: true },
          ]} emptyText="No revenue in this window">
            {regels.map(({ m, plat, d }) => (
              <tr key={`${m.id}-${plat}`}>
                <td><ModelNaam slug={m.slug} naam={m.stage_name} vet /></td>
                <td><Plat value={plat} /></td>
                <td className="num mono">{eur(d.gross_eur)}</td>
                <td className="num mono">{eur(d.platform_fee)}</td>
                <td className="num mono" title={aandeelLabel(plat)}>
                  {aandeelGeld(d.model_fee, d.gross_eur)}
                  <div className="mut2" style={{ fontSize: 11 }}>
                    {isFc(plat) ? 'paid by FanCentro' : 'model share'}
                    {d.model_share_pct != null ? ` · ${pct(d.model_share_pct)}` : ''}
                  </div>
                  {d.rijen_zonder_split && d.model_fee != null ? (
                    <div className="mut2" style={{ fontSize: 11 }}
                         title="Sales on this platform without a split for this model">
                      +{num(d.rijen_zonder_split)} without split
                    </div>
                  ) : null}
                </td>
                <td className="num mono">{aandeelGeld(d.agency_net, d.gross_eur)}</td>
                <td className="num mono">{num(m.conversations)}</td>
                <td className="num mono">{num(m.unique_fans)}</td>
              </tr>
            ))}
            {regels.length ? (
              <>
                {platform !== 'f2f' && regelsFc.length ? <TotaalRij label="Total FanCentro" plat="fancentro" t={totFc} /> : null}
                {platform !== 'fancentro' && regelsF2f.length ? <TotaalRij label="Total F2F" plat="f2f" t={totF2f} /> : null}
                <TotaalRij label="Grand total" t={totAlles} vet />
              </>
            ) : null}
          </Table>
        )}
      </Panel>

      <Panel inklap="earnings.top-spenders" title="Top Spenders" desc={`${num(spenders.data?.total)} fans`} flush>
        {spenders.loading ? <Loading /> : (
          <Table cols={[
            { key: 'r', label: '#' }, { key: 'f', label: 'Fan' }, { key: 'm', label: 'Model' },
            { key: 'g', label: 'Gross', num: true }, { key: 'n', label: 'Net', num: true },
          ]} emptyText="No spenders in this window">
            {(spenders.data?.spenders ?? []).map((s, i) => (
              <tr key={`${s.fan_id}-${i}`}>
                <td className="rank">{i + 1}</td>
                <td>{s.username}</td>
                <td><ModelNaam naam={s.model} /></td>
                <td className="num mono">{eur(s.gross_spend)}</td>
                <td className="num mono mut2">{eur(s.net_spend)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Panel inklap="earnings.model-earnings"
        title="Model earnings"
        desc="Each model’s % from the payout split for that platform. The platform keeps 20% of what the fan pays (FanCentro: its real cut); model share = the rest × the model’s %, per transaction. FanCentro pays its models directly. “Split not set” means no split for that platform (not a real 0%)."
        flush
      >
        <Table cols={[
          { key: 'p', label: 'Platform' }, { key: 'm', label: 'Model' },
          { key: 'g', label: 'Gross', num: true }, { key: 'pc', label: '%', num: true },
          { key: 'mf', label: 'Model share', num: true },
          { key: 'pf', label: 'Platform fee', num: true }, { key: 'n', label: 'Net', num: true },
        ]} emptyText="No models">
          {regels.map(({ m, plat, d }) => (
            <tr key={`${m.id}-${plat}`}>
              <td><Plat value={plat} /></td>
              <td><ModelNaam slug={m.slug} naam={m.stage_name} /></td>
              <td className="num mono">{eur(d.gross_eur)}</td>
              <td className="num mono" style={{ color: 'var(--acc2)' }}>
                {d.model_share_pct != null ? pct(d.model_share_pct)
                  : Number(d.gross_eur) > 0 ? <NogNietBekend /> : <span className="dim">—</span>}
              </td>
              <td className="num mono" title={aandeelLabel(plat)}>
                {aandeelGeld(d.model_fee, d.gross_eur)}
              </td>
              <td className="num mono">{eur(d.platform_fee)}</td>
              <td className="num mono">{eur(d.net_eur)}</td>
            </tr>
          ))}
        </Table>
      </Panel>

      <Panel inklap="earnings.transacties" title="Transactions" desc="summarised per event" flush>
        {txns.loading ? <Loading /> : (
          <Table cols={[
            { key: 'dt', label: 'Date / type' }, { key: 'fm', label: 'Fan / model' },
            { key: 'c', label: 'Chatter' }, { key: 'g', label: 'Gross', num: true },
            { key: 'mf', label: 'Model', num: true }, { key: 'pf', label: 'Platform', num: true },
            { key: 'n', label: 'Net', num: true }, { key: 'cc', label: 'Chatter', num: true },
            { key: 'rf', label: 'Referral', num: true }, { key: 'nn', label: 'Net-net', num: true },
          ]} emptyText="No transactions in this period.">
            {(txns.data?.items ?? []).map((t) => {
              const nettoBekend = t.net != null
              const netto = t.net ?? 0
              // De verkoper volgens de kaartregel; valt terug op wat de
              // transactieroute zelf voorstelt als deze verkoop niet aan een
              // chatter is toegerekend (dan staat er ook geen bedrag).
              const naam = verkoperVan.get(Number(t.id)) ?? null
              const pct = naam ? (commissiePct.get(naam) ?? 0) : 0
              const telt = t.counts_for_commission !== false && nettoBekend
              const commissie = telt ? netto * (pct / 100) : 0
              const referral = telt ? netto * ((naam ? leadPct.get(naam, t.sale_at_utc ?? t.created_at) : 0) / 100) : 0
              return (
                <tr key={`c-${t.id}`} data-rij={`c-${t.id}`} style={String(t.id) === laatsteRij ? { background: 'rgba(124,92,255,.14)', boxShadow: 'inset 3px 0 0 var(--acc)' } : undefined}>
                  <td className="mut2" style={{ fontSize: 12 }}>
                    {dtTx(t.sale_at_utc ?? t.created_at)}
                    <div className="mut"><Plat value={t.platform} /> {String(t.event_type ?? '').replace(/_/g, ' ')}</div>
                  </td>
                  <td>
                    <b><NaarGesprek t={t}>{t.fan_username || t.fan_platform_id || '—'}</NaarGesprek></b>
                    <div className="mut2" style={{ fontSize: 12 }}>
                      <ModelNaam slug={t.model_slug} naam={t.model_name} size={16} />
                    </div>
                  </td>
                  <td>{naam ?? <span className="mut">Not assigned yet</span>}</td>
                  <td className="num mono">{eur(t.gross)}</td>
                  <td className="num mono" title={aandeelLabel(t.platform)}>
                    {modelGeld(t.model_fee)}
                    {isFc(t.platform) && t.model_fee != null ? (
                      <div className="mut2" style={{ fontSize: 11 }}>paid by FanCentro</div>
                    ) : null}
                  </td>
                  <td className="num mono">{t.platform_fee == null ? <NogNietBekend /> : geld(t.platform_fee)}</td>
                  <td className="num mono">{geld(t.net)}</td>
                  <td className="num mono">{commissie ? eur(commissie) : '—'}</td>
                  <td className="num mono">{referral ? eur(referral) : '—'}</td>
                  <td className="num mono">
                    {nettoBekend ? eur(netto - commissie - referral) : <span className="mut">—</span>}
                  </td>
                </tr>
              )
            })}
          </Table>
        )}
      </Panel>

      <Panel inklap="earnings.transacties-detail" title="Transactions — detail"
             desc={`${num(txns.data?.total)} in this window`} flush>
        {txns.loading ? <Loading /> : (
          <Table cols={[
            { key: 'd', label: 'Date' }, { key: 'p', label: 'Plat.' },
            { key: 't', label: 'Type' }, { key: 'f', label: 'Fan' },
            { key: 'm', label: 'Model' }, { key: 'c', label: 'Chatter' },
            { key: 'g', label: 'Gross', num: true }, { key: 'mf', label: 'Model', num: true },
            { key: 'pf', label: 'Platform', num: true }, { key: 'n', label: 'Net', num: true },
            { key: 'cc', label: 'Chatter', num: true }, { key: 'rf', label: 'Referral', num: true },
            { key: 'nn', label: 'Net-net', num: true },
          ]} emptyText="No transactions in this period.">
            {(txns.data?.items ?? []).map((t) => {
              const nettoBekend = t.net != null
              const netto = t.net ?? 0
              const naam = t.assigned_chatter_name
              const telt = t.counts_for_commission !== false && nettoBekend
              const commissie = telt ? netto * ((naam ? commissiePct.get(naam) ?? 0 : 0) / 100) : 0
              const referral = telt ? netto * ((naam ? leadPct.get(naam, t.sale_at_utc ?? t.created_at) : 0) / 100) : 0
              return (
                <tr key={t.id} data-rij={`d-${t.id}`} style={String(t.id) === laatsteRij ? { background: 'rgba(124,92,255,.14)', boxShadow: 'inset 3px 0 0 var(--acc)' } : undefined}>
                  <td className="mut2" style={{ fontSize: 12 }}>
                    {dtTx(t.sale_at_utc ?? t.created_at)}
                  </td>
                  <td><Plat value={t.platform} /></td>
                  <td className="mut">{typeLabel(t.event_type)}</td>
                  <td><NaarGesprek t={t}>{t.fan_username || t.fan_platform_id || <span className="dim">—</span>}</NaarGesprek></td>
                  <td><ModelNaam slug={t.model_slug} naam={t.model_name} /></td>
                  {/* Oud zet hier geen dialoog maar een uitklaplijst in de
                      rij zelf: kiezen wijst de verkoop meteen toe, leeg maakt
                      hem weer los, en "organic" zegt dat er geen chatter
                      achter zit. */}
                  <td onClick={(e) => e.stopPropagation()}>
                    <select
                      className="inp cell wide"
                      value={lokaleKeuze[t.id]?.keuze ?? keuzeVanServer(t)}
                      onChange={(e) => wijsToe(t, e.target.value)}
                    >
                      <option value="auto">Automatic rule</option>
                      <option value="unassigned">Not assigned yet</option>
                      <option value="organic">Organic (no chatter)</option>
                      {(comp.data ?? []).map((c) => (
                        <option key={c.chatter_id} value={c.chatter_id}>{c.name}</option>
                      ))}
                    </select>
                    {t.assigned_manual ? (
                      <span className="hint">
                        manual{t.assigned_by_name ? ` · ${t.assigned_by_name}` : ''}
                        {t.assigned_at ? ` · ${dtTx(t.assigned_at)}` : ''}
                      </span>
                    ) : t.reviewed === false && naam ? (
                      <span className="hint">automatic · not reviewed</span>
                    ) : null}
                  </td>
                  <td className="num mono">{eur(t.gross)}</td>
                  <td className="num mono" title={aandeelLabel(t.platform)}>
                    {modelGeld(t.model_fee)}
                    {isFc(t.platform) && t.model_fee != null ? (
                      <div className="mut2" style={{ fontSize: 11 }}>paid by FanCentro</div>
                    ) : null}
                  </td>
                  <td className="num mono">{t.platform_fee == null ? <NogNietBekend /> : geld(t.platform_fee)}</td>
                  <td className="num mono">{geld(t.net)}</td>
                  <td className="num mono">{commissie ? eur(commissie) : '—'}</td>
                  <td className="num mono">{referral ? eur(referral) : '—'}</td>
                  <td className="num mono">
                    {nettoBekend ? eur(netto - commissie - referral) : <span className="mut">—</span>}
                  </td>
                </tr>
              )
            })}
          </Table>
        )}
      </Panel>

      <Panel inklap="earnings.sales-per-chatter"
        title="Sales per chatter"
        // 11-09-2026 (Jordy): "Max heeft 1,84 open staan maar er staat 0 euro."
        // In haar rij stond SALES 0 en COMMISSION € 0,00 naast OPEN € 1,84, en
        // dat las als tegenspraak. Het is er geen: de linkerkolommen volgen de
        // gekozen periode (die standaard op Today staat, en vandaag was er
        // niets), en Open en Paid zijn van alle tijd. Twee tijdvakken in één
        // tabel zonder dat er iets bij stond. Nu staat het er.
        // 16-09-2026 (Jordy, CHAT-015): een handmatig toegewezen verkoop kwam
        // hier keurig binnen (Sales 1, Gross €15,00, Net €8,40), maar
        // Commission bleef €0,00 en dat las als een rekenfout. Het is er geen:
        // zolang er geen overgangsdatum staat, telt er niets mee voor
        // commissie (EARN-004B). Een streepje in plaats van €0,00 zegt dat
        // eerlijk, en de regel eronder zegt waarom.
        desc={`${num(sales.data?.attributed_sales)} of ${num(sales.data?.total_sales)} attributed · `
          + 'Sales, Gross, Net and Commission follow the selected period; '
          + 'Open and Paid are all-time.'
          + (commissieUit ? ' · Commission starts at the transition date — not counted yet.' : '')}
        flush
        right={
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            {(balances.data?.per_chatter ?? []).some((b) => b.deleted) ? (
              <label className="chkrow" style={{ cursor: 'pointer', fontSize: 12.5 }}
                     onClick={() => setToonVerwijderd((v) => !v)}>
                <span className={toonVerwijderd ? 'chk on' : 'chk'}>{toonVerwijderd ? '✓' : ''}</span>
                Show deleted chatters
              </label>
            ) : null}
            <button className="btn sm" type="button"
                    onClick={() => setPayFor(balances.data?.per_chatter?.[0]?.chatter_id ?? null)}>
              Record payment
            </button>
          </div>
        }
      >
        <Table cols={[
          { key: 'c', label: 'Chatter' }, { key: 's', label: 'Sales', num: true },
          { key: 'g', label: 'Gross', num: true }, { key: 'n', label: 'Net', num: true },
          { key: 'p', label: '%', num: true }, { key: 'cm', label: 'Commission', num: true },
          { key: 'r', label: 'Referral', num: true }, { key: 'o', label: 'Open', num: true },
          { key: 'pd', label: 'Paid', num: true }, { key: 'a', label: 'Action' },
        ]} emptyText="No sales in this period.">
          {/* Oud voedt deze tabel uit TWEE bronnen: de venstercijfers
              (sales-by-chatter: sales, gross, net, commissie, referral) en de
              saldi (balances: open, betaald, en de uitsplitsing van open).
              De commissie is dus die van het gekozen venster, niet het
              totaal-ooit — vandaar dat Nour hier €12.36 toont en niet de
              €13.20 die zij in totaal heeft verdiend.

              De volgorde is die van de API; oud sorteert niet zelf. */}
          {(sales.data?.per_chatter ?? []).map((s) => {
            const b = (balances.data?.per_chatter ?? [])
              .find((x) => x.chatter_id === s.chatter_id)
            // 30-09-2026 (Jordy): een verwijderde chatter blijft staan zolang er
            // nog iets open staat (dan kan het via Record payment worden
            // afgeboekt); met €0,00 open alleen als je erom vraagt.
            const verwijderd = Boolean(s.deleted || b?.deleted)
            if (verwijderd && !toonVerwijderd && Math.abs(Number(b?.open_commission || 0)) < 0.005) return null
            // Oud valt terug op het commissiepercentage als een chatter geen
            // eigen commissiebedrag heeft meegekregen.
            const commissie = Number(
              s.own_commission ?? s.commission_amount
              ?? (Number(s.gross || 0) * (s.commission_pct ?? 15)) / 100)
            const referral = Number(s.referral_earned || 0)
            const openRef = Number(b?.open_referral_commission || 0)
            const voorschot = Number(b?.advance_remaining || 0)
            return (
              // Een chatter zonder verkopen in dit venster staat gedimd, maar
              // blijft staan — oud toont de volledige set.
              <tr key={s.chatter_id} style={{ opacity: s.sales === 0 ? 0.5 : 1 }}>
                <td>
                  <b>{s.name ?? nameOf.get(s.chatter_id) ?? s.chatter_id}</b>
                  {verwijderd ? <>{' '}<Tag kind="mut">Deleted</Tag></> : null}
                  {s.is_lead ? (
                    <>{' '}<Tag kind="acc">
                      Lead{s.lead_override_pct != null ? ` ${num(s.lead_override_pct)}%` : ''}
                    </Tag></>
                  ) : null}
                </td>
                <td className="num mono">{num(s.sales)}</td>
                <td className="num mono">{eurPunt(s.gross)}</td>
                <td className="num mono">{eurPunt(s.net)}</td>
                <td className="num mono">{s.commission_pct ?? 15}%</td>
                <td className="num mono">
                  {commissieUit ? <span className="mut">—</span> : eurPunt(commissie)}
                </td>
                {/* Alleen een lead kán referral verdienen; bij de rest zet oud
                    een streepje in plaats van €0.00. */}
                <td className="num mono">
                  {commissieUit || !s.is_lead ? '—' : eurPunt(referral)}
                </td>
                <td className="num mono">
                  {commissieUit ? <span className="mut">—</span> : eurPunt(b?.open_commission ?? 0)}
                  {openRef > 0.005 || voorschot > 0.005 ? (
                    <div className="mut2" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      Own {eurPunt(b?.open_own_commission ?? 0)}
                      {openRef > 0.005 ? ` · referral ${eurPunt(openRef)}` : ''}
                    </div>
                  ) : null}
                  {voorschot > 0.005 ? (
                    <div style={{ fontSize: 12, whiteSpace: 'nowrap', color: 'var(--acc2)' }}>
                      Advance {eurPunt(voorschot)}
                    </div>
                  ) : null}
                </td>
                <td className="num mono">{eurPunt(b?.paid_commission ?? 0)}</td>
                <td>
                  <button className="btn ghost sm" type="button"
                          onClick={() => setPayFor(s.chatter_id)}>
                    Record payment
                  </button>
                </td>
              </tr>
            )
          })}
          {/* Verkopen die aan niemand hangen: oud zet ze als eigen regel onder
              de chatters, met streepjes vanaf de commissiekolom. */}
          {(sales.data?.unattributed?.sales ?? 0) > 0 ? (
            <tr style={{ opacity: 0.7 }}>
              <td className="mut" style={{ fontStyle: 'italic' }}>
                Unassigned (platform-direct / no chat match)
              </td>
              <td className="num mono">{num(sales.data?.unattributed?.sales)}</td>
              <td className="num mono">{eurPunt(sales.data?.unattributed?.gross)}</td>
              <td className="num mono">{eurPunt(sales.data?.unattributed?.net)}</td>
              <td className="num">—</td>
              <td className="num">—</td>
              <td className="num">—</td>
              <td className="num">—</td>
              <td className="num">—</td>
              <td className="num">—</td>
            </tr>
          ) : null}
          {(sales.data?.not_attributable?.sales ?? 0) > 0 ? (
            <tr style={{ opacity: 0.7 }}>
              <td className="mut" style={{ fontStyle: 'italic' }}
                  title="Sales without a chatter that cannot be credited automatically (e.g. subscriptions without a manual assignment)">
                Not attributable ({Object.entries(sales.data?.not_attributable?.per_type ?? {})
                  .map(([k, n]) => `${String(k).replace(/_/g, ' ')} ${n}`).join(', ') || 'other'})
              </td>
              <td className="num mono">{num(sales.data?.not_attributable?.sales)}</td>
              <td className="num mono">{eurPunt(sales.data?.not_attributable?.gross)}</td>
              <td className="num mono">{eurPunt(sales.data?.not_attributable?.net)}</td>
              <td className="num">—</td>
              <td className="num">—</td>
              <td className="num">—</td>
              <td className="num">—</td>
              <td className="num">—</td>
              <td className="num">—</td>
            </tr>
          ) : null}
          {/* Het totaal telt sales/gross/net inclusief de niet-toegewezen en de
              niet-toe-te-rekenen regel (= total_sales); commissie en referral
              alleen over de chatters. */}
          <tr style={{ fontWeight: 700 }}>
            <td>Total</td>
            <td className="num mono">
              {num(som((x) => x.sales) + (sales.data?.unattributed?.sales ?? 0)
                + (sales.data?.not_attributable?.sales ?? 0))}
            </td>
            <td className="num mono">
              {eurPunt(som((x) => x.gross) + (sales.data?.unattributed?.gross ?? 0)
                + (sales.data?.not_attributable?.gross ?? 0))}
            </td>
            <td className="num mono">
              {eurPunt(som((x) => x.net) + (sales.data?.unattributed?.net ?? 0)
                + (sales.data?.not_attributable?.net ?? 0))}
            </td>
            <td className="num">—</td>
            <td className="num mono">
              {commissieUit ? '—' : eurPunt(som((x) => Number(
                x.own_commission ?? x.commission_amount
                ?? (Number(x.gross || 0) * (x.commission_pct ?? 15)) / 100)))}
            </td>
            <td className="num mono">
              {commissieUit ? '—' : eurPunt(som((x) => x.referral_earned))}
            </td>
            <td className="num mono">
              {commissieUit ? '—' : eurPunt(somSaldo((b) => b.open_commission))}
            </td>
            <td className="num mono">{eurPunt(somSaldo((b) => b.paid_commission))}</td>
            <td />
          </tr>
        </Table>
      </Panel>

      <Panel inklap="earnings.subscriptions"
        title="Subscriptions pushed by chatters"
        flush
        right={
          <>
            <label className="chkrow">
              <span className={onlyPitched ? 'chk on' : 'chk'}
                    onClick={() => setOnlyPitched((v) => !v)}>{onlyPitched ? '✓' : ''}</span>
              Show pitched only
            </label>
            <span className="mut2" style={{ fontSize: 12 }}>
              {num(pitches.data?.summary?.pitched ?? 0)} of{' '}
              {num(pitches.data?.summary?.total ?? 0)} pitched
            </span>
            {/* Uit staat auto-assign alleen bij precies één pitcher aan, en het
                overschrijft nooit een handmatige toewijzing. Dat hoort erbij te
                staan, anders leest "OFF" als een defect. */}
            {isOwner ? (
              <button className="btn ghost sm" type="button"
                      title={'When on, a subscription is credited automatically only when '
                           + 'exactly one chatter pitched it in the 7 days before, and a '
                           + 'manual assignment is never overwritten.'}
                      onClick={() => {
                        const aan = !pitches.data?.auto_assign_enabled
                        if (!window.confirm(aan
                          ? 'Turn auto-assign ON? Subscriptions pitched by exactly one chatter are then credited automatically.'
                          : 'Turn auto-assign OFF? Pitches stay suggestions you assign by hand.')) return
                        run({
                          label: aan ? 'Auto-assign on' : 'Auto-assign off',
                          request: mut.put('/api/admin/chatter-finance/subscription-pitch-settings',
                            { auto_assign: aan }),
                          onDone: pitches.refetch,
                          successText: aan ? 'Auto-assign turned ON.' : 'Auto-assign turned OFF.',
                        })
                      }}>
                Auto-assign: {pitches.data?.auto_assign_enabled ? 'ON' : 'OFF'}
              </button>
            ) : (
              <span className="mut2" style={{ fontSize: 12 }}
                    title={'When on, a subscription is credited automatically only '
                         + 'when exactly one chatter pitched it, and a manual '
                         + 'assignment is never overwritten.'}>
                Auto-assign: {pitches.data?.auto_assign_enabled ? 'ON' : 'OFF'} ⓘ
              </span>
            )}
          </>
        }
      >
        <Table cols={[
          { key: 's', label: 'Subscription' }, { key: 'f', label: 'Fan' },
          { key: 'm', label: 'Model' }, { key: 'g', label: 'Gross', num: true },
          { key: 'p', label: 'Pitched by' }, { key: 'msg', label: 'Message' },
          { key: 'w', label: 'When' }, { key: 'a', label: 'Assign' },
        ]} emptyText="Subscriptions with a pitch in the 7 days before appear here.">
          {/* De veldnamen hieronder komen uit de API; ze stonden verkeerd
              (subscription_id/fan/model/gross/pitched_by/message/when) en
              daardoor toonde deze tabel overal €0,00 met lege kolommen. */}
          {(pitches.data?.rows ?? []).filter((row) => filter.past(row.model_stage_name)).map((row) => {
            const lijst = row.pitches && row.pitches.length ? row.pitches : []
            const laatste = lijst.length ? lijst[lijst.length - 1].message_id : 0
            const gekozenId = pitchKeuze[row.revenue_event_id] || laatste
            const gekozen = lijst.find((p) => p.message_id === gekozenId) || lijst[lijst.length - 1] || null
            const tekst = gekozen?.text || row.pitch_text || ''
            const gesprek = gekozen?.conversation_id || row.conversation_id || 0
            const toewijzen = (chatterId: number) => run({
              label: 'Assign sale',
              request: mut.post('/api/admin/chatter-finance/sale-override', {
                revenue_event_id: row.revenue_event_id,
                chatter_id: chatterId,
                reason: `subscription pitched ${gekozen?.gap_label || row.pitch_gap_label || ''} before`.trim(),
              }),
              onDone: pitches.refetch,
              successText: 'Sale assigned.',
            })
            return (
            <tr key={row.revenue_event_id}>
              <td className="mut2 mono">{row.revenue_event_id}</td>
              <td>{row.fan_display_name || row.fan_handle || '—'}</td>
              <td>{row.model_stage_name ? <ModelNaam naam={row.model_stage_name} /> : '—'}</td>
              <td className="num mono">{eur(row.amount)}</td>
              <td>{row.assigned_chatter_id
                    ? <Tag kind={row.auto_assigned ? 'acc' : 'ok'}>
                        {(row.assigned_chatter_name || `#${row.assigned_chatter_id}`)
                          + (row.auto_assigned ? ' (auto)' : '')}</Tag>
                    : row.is_ambiguous
                      ? <Tag kind="warn">{(row.pitching_chatter_names || []).join(' / ') || 'several'}</Tag>
                      : row.pitch_chatter_name || <span className="dim">no pitch found</span>}</td>
              <td className="mut" style={{ maxWidth: 320 }}>
                {lijst.length > 1 && (
                  <select className="inp" style={{ maxWidth: 300, marginBottom: 4, fontSize: 12 }}
                          value={String(gekozen?.message_id ?? laatste)}
                          onChange={(e) => setPitchKeuze((k) => ({ ...k, [row.revenue_event_id]: Number(e.target.value) }))}>
                    {lijst.map((p, i) => {
                      const kort = String(p.text || '').replace(/\s+/g, ' ').slice(0, 56)
                      return (
                        <option key={p.message_id} value={String(p.message_id)}>
                          {`${i + 1}/${lijst.length} · ${p.chatter_name || 'chatter'}`
                            + (p.gap_label ? ` · ${p.gap_label} before` : '')
                            + (kort ? ` — ${kort}${String(p.text || '').length > 56 ? '…' : ''}` : '')}
                        </option>
                      )
                    })}
                  </select>
                )}
                <div>{tekst || '—'}</div>
                {gesprek > 0 && (
                  <Link className="mut2" style={{ fontSize: 11 }} to={`/conversations/show/${gesprek}`}>
                    Open chat →</Link>
                )}
              </td>
              <td className="mut2" style={{ fontSize: 12 }}>
                {gekozen?.gap_label || row.pitch_gap_label
                  ? `pitched ${gekozen?.gap_label || row.pitch_gap_label} before`
                  : (row.pitch_at ? dt(row.pitch_at) : '—')}</td>
              <td>
                {row.assigned_chatter_id
                  ? <span className="mut2" style={{ fontSize: 12 }}>assigned</span>
                  : row.is_ambiguous
                    ? (row.pitching_chatter_ids || []).map((cid, i) => (
                        <button key={cid} className="btn ghost sm" type="button" disabled={!!busy}
                                style={{ marginRight: 4 }} onClick={() => toewijzen(cid)}>
                          {(row.pitching_chatter_names || [])[i] || `#${cid}`}
                        </button>
                      ))
                    : row.pitch_chatter_id
                      ? <button className="btn ghost sm" type="button" disabled={!!busy}
                                onClick={() => toewijzen(row.pitch_chatter_id as number)}>
                          Assign to {row.pitch_chatter_name || `#${row.pitch_chatter_id}`}
                        </button>
                      : <span className="dim">—</span>}
              </td>
            </tr>
            )
          })}
        </Table>
      </Panel>

      <Panel inklap="earnings.payment-history" title="Payment history" flush>
        <Table cols={[
          { key: 'c', label: 'Chatter' }, { key: 'p', label: 'Period' },
          { key: 's', label: 'Sales', num: true },
          { key: 'ce', label: 'Commission earned', num: true },
          { key: 'pd', label: 'Paid', num: true }, { key: 'e', label: 'Extra' },
          { key: 'd', label: 'Date' }, { key: 'n', label: 'Note' },
          { key: 'a', label: 'Action' },
        ]} emptyText="No payouts yet">
          {/* Oud sorteert deze lijst zelf: nieuwste betaling bovenaan, op
              `paid_at` en anders op het einde van de periode. De API levert
              alleen op periode-einde gesorteerd, dus bij gelijke perioden zou
              de volgorde anders uitvallen. */}
          {[...(cpay.data?.items ?? [])]
            .sort((a, b) => String(b.paid_at || b.period_end || '')
              .localeCompare(String(a.paid_at || a.period_end || '')))
            .map((r) => (
            <tr key={r.id}>
              <td><b>{nameOf.get(r.chatter_id) ?? r.chatter_id}</b></td>
              <td className="mut2" style={{ fontSize: 12 }}>
                {/* Oud toont alleen de dagen, met een pijl ertussen. */}
                {dtDay(r.period_start)} → {dtDay(r.period_end)}
              </td>
              <td className="num mono">{eurPunt(r.gross_amount)}</td>
              <td className="num mono">{eurPunt(r.commission_amount)}</td>
              <td className="num mono">{eurPunt(r.amount_paid)}</td>
              {/* "Extra" is bij oud het bedrag dat bóven de verdiende
                  commissie is uitbetaald: een bonus, of een voorschot dat
                  later wordt verrekend. Is er niets extra's, dan een streepje. */}
              <td>{(() => {
                const betaald = Number(r.amount_paid ?? r.commission_amount ?? 0)
                const extra = betaald - Number(r.commission_amount || 0)
                return extra > 0.005
                  ? <Tag kind="acc">
                      {r.extra_is_bonus === false ? 'Advance' : 'Bonus'} {eurPunt(extra)}
                    </Tag>
                  : <span className="dim">—</span>
              })()}</td>
              <td className="mut2" style={{ fontSize: 12 }}>{dtDay(r.paid_at) || '—'}</td>
              <td className={r.note ? 'mut' : 'dim'}>{r.note || ''}</td>
              <td>
                <button
                  className="btn ghost sm"
                  type="button"
                  onClick={() => run({
                    label: 'Delete payout',
                    confirm: 'Delete this payout?',
                    request: mut.del(`/api/admin/chatter-finance/payouts/${r.id}`),
                    onDone: cpay.refetch,
                  })}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </Table>
      </Panel>

      <Panel inklap="earnings.documenten"
        title="Financial Documents"
        flush
        right={
          <>
            <Seg options={DOCS} value={docTab} onChange={setDocTab} />
            <button
              className="btn sm"
              type="button"
              disabled={busy === 'Refresh from platforms'}
              onClick={() => run({
                label: 'Refresh from platforms',
                request: mut.post('/api/admin/dashboard/payouts/refresh'),
                onDone: ppay.refetch,
                successText: 'Documents fetched.',
              })}
            >
              Refresh from platforms
            </button>
          </>
        }
      >
        <Table cols={[
          { key: 'p', label: 'Platform' }, { key: 'pe', label: 'Period' },
          { key: 'pa', label: 'Paid at' }, { key: 'g', label: 'Gross', num: true },
          { key: 'n', label: 'Net', num: true }, { key: 's', label: 'Status' },
          { key: 'd', label: 'Document' },
        ]} emptyText="No documents">
          {/* De twee knoppen filteren op status: Uitbetalingen toont
              status=payout, Facturen / btw toont status=vat. */}
          {(ppay.data?.items ?? [])
            .filter((r) => (docTab === 'payouts' ? r.status === 'payout' : r.status !== 'payout'))
            .map((r) => (
            <tr key={r.id}>
              <td><Plat value={r.platform} /></td>
              <td className="mut">{dt(r.period_start)} – {dt(r.period_end)}</td>
              <td className="mut2">{r.paid_at ? dt(r.paid_at) : '—'}</td>
              <td className="num mono">{eurPunt(r.amount_gross)}</td>
              <td className="num mono">{eurPunt(r.amount_net)}</td>
              <td><Tag kind={r.status === 'payout' ? 'ok' : 'mut'}>{r.status}</Tag></td>
              <td>
                {r.has_document
                  ? <button className="btn ghost sm" type="button"
                            onClick={() => downloadDoc(r.id)}>📄 PDF</button>
                  : <span className="dim">—</span>}
              </td>
            </tr>
          ))}
        </Table>
      </Panel>
      {/* Het betaalvenster staat apart, want het heeft méér nodig dan een
          bedrag: periode, omzet, de uitsplitsing van het openstaande saldo en
          het onderscheid bonus/voorschot. Zonder die velden weigert de server
          de POST (`MarkPaidRequest` heeft er geen standaardwaarden voor). */}
      {payFor != null ? (
        <PayoutDialog
          chatterId={payFor}
          namen={nameOf}
          balances={balances.data?.per_chatter ?? []}
          commissionSince={balances.data?.commission_since ?? null}
          betalingen={cpay.data?.items ?? []}
          wallets={comp.data ?? null}
          onChatterChange={setPayFor}
          onClose={() => setPayFor(null)}
          onDone={() => { cpay.refetch(); balances.refetch(); sales.refetch() }}
        />
      ) : null}
    </>
  )
}


/**
 * 22-10-2026 (Jordy): vangnet. Betaalde content (FC/F2F) die na 15 minuten nog geen
 * chatter heeft en niet handmatig is toegewezen, staat hier bovenaan, tot iemand hem
 * toewijst of de koppeling alsnog lukt. Leeg = niets in beeld.
 */
function OnbeheerdBalk() {
  const q = useApi<{ items: { id: number; created_at: string; platform: string; amount: number;
    model: string | null; conversation_id: number | null; reden: string | null }[] }>(
    '/api/admin/dashboard/earnings/onbeheerd')
  usePoll(60000, q.refetch)
  const items = q.data?.items ?? []
  if (!items.length) return null
  return (
    <div role="alert" data-testid="onbeheerd-balk"
         style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8,
                  border: '1px solid rgba(248,113,113,0.45)', borderLeft: '3px solid var(--bad)',
                  background: 'rgba(248,113,113,0.08)', fontSize: 12.5 }}>
      <b>{items.length} paid {items.length === 1 ? 'sale' : 'sales'} without a chatter</b>
      <span className="mut2"> · last 3 days · assign in Transactions below</span>
      <div style={{ display: 'grid', gap: 2, marginTop: 6 }}>
        {items.slice(0, 8).map((v) => (
          <div key={v.id} className="mut" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <span>{dtShort(v.created_at)}</span>
            <b>{eur(v.amount)}</b>
            <span>{v.model ?? '—'} · {String(v.platform).toUpperCase()}</span>
            {v.conversation_id ? <Link to={`/conversations/show/${v.conversation_id}`}>open chat</Link> : null}
          </div>
        ))}
        {items.length > 8 ? <div className="mut2">+{items.length - 8} more</div> : null}
      </div>
    </div>
  )
}
